import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SchoolBellEAPIService } from './generated-api/schoolbell-e/schoolbell-e-api';

export type StaffInvitationStatus = 'Pending' | 'Accepted' | 'Rejected' | 'Cancelled' | 'Expired';

export interface StaffMe {
  staffId: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  role: string;
  invitationStatus: StaffInvitationStatus;
  /** 초대 수락 직후 부여된 권한이 하나도 없는 상태인지 — invitation.component.ts#accept() 참고. */
  hasEffectiveScopes: boolean;
}

export interface PendingInvitation {
  invitationId: string;
  role: string;
  invitedByStaffId: string;
  expiresAt: string;
}

/**
 * schoolbell-e-api `/staff/auth/*`(자체 로그인 — User.phone + password 인증 후 Staff 소속 확인)
 * 를 소비하는 클라이언트 쪽 책임 — 생성된 `SchoolBellEAPIService` 를 감싼다
 * (`OAuthClientReviewService` 와 동일 패턴; 백엔드 컨트롤러가 `@ApiOkResponse` 타입을 선언하지
 * 않아 응답 제네릭은 호출부에서 명시한다).
 *
 * 세션 자체는 schoolbell-e-api 가 발급하는 host-only HttpOnly 쿠키
 * (`schoolbell-e-staff-session`) 로 유지된다 — SPA 는 토큰을 소유하지 않고, 매 요청
 * `withCredentials`(staffCredentialsInterceptor)로 쿠키만 실어 보낸다.
 *
 * `staff` signal 은 로그인/`me()` 성공 시점의 표시용 캐시일 뿐 세션의 SoT가 아니다 — 실제
 * 인가는 항상 서버(쿠키 검증)가 한다.
 */
@Injectable({ providedIn: 'root' })
export class StaffAuthService {
  private readonly api = inject(SchoolBellEAPIService);

  private readonly _staff = signal<StaffMe | null>(null);
  readonly staff = this._staff.asReadonly();

  private readonly _sessionChecked = signal(false);
  /**
   * 최초 세션 확인(`me()`) 완료 여부 — 성공·실패 무관하게 첫 호출 후 true
   * (`@sbe-spinoff-client/staff-oidc-auth`의 `StaffOidcAuthService.sessionChecked`와 동일
   * 패턴, `injectProtectedEntry` 기반 부팅 로딩 오버레이 게이팅용, 2026-08-13).
   * `logout()`에서는 되돌리지 않는다 — 되돌리면 guard 없는 경로(`/welcome` 등)에서
   * `isProtectedEntry && !sessionChecked()`가 다시 true로 걸려 로그아웃 직후 오버레이가
   * 재노출되고, 그 경로엔 guard가 없어 sessionChecked가 다시 true로 안 돌아와 영구히
   * 멈춘다(`StaffOidcAuthService.logout()` 주석과 동일 근거, 2026-08-11 코드 리뷰로 발견된
   * 버그를 그대로 재발시키지 않기 위함).
   */
  readonly sessionChecked = this._sessionChecked.asReadonly();

  /**
   * 이 SPA 인스턴스에서 방금 비밀번호로 로그인했는지 — OIDC interaction 확인 화면
   * (`oauth-authorize`)이 "방금 인증한 같은 사용자"에게 계정 확인을 또 묻지 않도록 읽는다
   * (OP `always_confirm_existing_account` 시나리오 1과 동일). 메모리에만 두므로 새로고침·재방문
   * (= 이전 방문의 세션 흔적)이면 false 라 확인 화면이 뜬다.
   */
  private justLoggedIn = false;

  /** `true`를 돌려주면 플래그를 소비한다(1회용). */
  consumeJustLoggedIn(): boolean {
    const value = this.justLoggedIn;
    this.justLoggedIn = false;
    return value;
  }

  async login(phone: string, password: string): Promise<StaffMe> {
    const res = await firstValueFrom(this.api.staffAuthControllerLogin<StaffMe>({ phone, password }));
    this._staff.set(res);
    this.justLoggedIn = true;
    return res;
  }

  async me(): Promise<StaffMe> {
    try {
      const res = await firstValueFrom(this.api.staffAuthControllerMe<StaffMe>());
      this._staff.set(res);
      return res;
    } finally {
      this._sessionChecked.set(true);
    }
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.api.staffAuthControllerLogout());
    } finally {
      this._staff.set(null);
      this.justLoggedIn = false;
    }
  }

  getInvitation(): Promise<PendingInvitation | null> {
    return firstValueFrom(this.api.staffAuthControllerInvitation<PendingInvitation | null>());
  }

  async acceptInvitation(): Promise<StaffMe> {
    const res = await firstValueFrom(this.api.staffAuthControllerAcceptInvitation<StaffMe>());
    this._staff.set(res);
    return res;
  }

  async rejectInvitation(reason?: string): Promise<void> {
    try {
      await firstValueFrom(this.api.staffAuthControllerRejectInvitation({ reason }));
    } finally {
      this._staff.set(null);
    }
  }

  /**
   * requiresStaff OIDC client(commerce-staff/sms-staff/grafana 등)의 interaction을 현재 staff
   * 세션으로 완료 — org/team 소속 검증·동의 화면 없이 즉시 로그인 처리(oidc-interaction.controller.ts
   * #interactionStaffLogin). 백엔드가 `@ApiOkResponse` 타입을 선언하지 않아(다른 컨트롤러와 동일
   * 사정, 위 login/me 참고) 응답 제네릭을 여기서 명시한다.
   */
  completeOidcInteraction(uid: string): Promise<{ redirectTo: string }> {
    return firstValueFrom(
      this.api.oidcInteractionControllerInteractionStaffLogin<{ redirectTo: string }>(uid),
    );
  }
}
